import { SortingOrder } from 'chaika-enums';
import { Optional } from 'chaika-common-types';

import { FilterValue, SorterResult, TableCurrentDataSource, TablePaginationConfig, TableProps } from '../types';
import type { LocalSortState } from '../VirtualTable/useLocalSort';

import { TableAction } from '@components/Table/VirtualTable';
import { removeNilAndEmptyKeys } from '@utils/removeNilAndEmptyKeys';

interface Props<TableData = object> {
    onChange?: TableProps<TableData>['onChange'];
    onFiltersChange?(params: Record<any, any>): Promise<void> | void;
    onSortingOrderChange?(order: SortingOrder): Promise<void>;
    writeColumnFiltersToUrl(filters: Record<any, any>): void;
    writeSortToUrl(sort: LocalSortState): void;
}

interface Data<TableData = object> {
    onTableChange(...args: Parameters<NonNullable<TableProps<TableData>['onChange']>>): Promise<void>;
}

export function useChangeTable<TableData = object>({
    onChange,
    onFiltersChange,
    onSortingOrderChange,
    writeColumnFiltersToUrl,
    writeSortToUrl,
}: Props<TableData>): Data<TableData> {
    // URL пишем до делегирования: иначе консьюмеры с собственным `onChange` не попадают в URL вовсе.
    const onTableChange = async (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<TableData> | SorterResult<TableData>[],
        extra: TableCurrentDataSource<TableData>,
    ): Promise<void> => {
        if (extra.action === TableAction.Filter) {
            writeColumnFiltersToUrl(removeNilAndEmptyKeys(filters));
        }
        if (extra.action === TableAction.Sort) {
            const sortState = readSortState(sorter);

            if (sortState) {
                writeSortToUrl(sortState);
            }
        }

        await (onChange ?? applyDefaultTableChange)(pagination, filters, sorter, extra);
    };

    async function applyDefaultTableChange(
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<TableData> | SorterResult<TableData>[],
        extra: TableCurrentDataSource<TableData>,
    ): Promise<void> {
        if (extra.action === TableAction.Filter) {
            await onFiltersChange?.(removeNilAndEmptyKeys(filters));
        }
        if (extra.action === TableAction.Sort) {
            const order = createQueryOrderParams(sorter);
            onSortingOrderChange?.(order);
        }
    }

    function createQueryOrderParams(sorter: any): SortingOrder {
        return sorter.order === 'ascend' ? SortingOrder.Asc : SortingOrder.Desc;
    }

    function readSortState(sorter: SorterResult<TableData> | SorterResult<TableData>[]): Optional<LocalSortState> {
        const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        const columnKey = singleSorter?.column?.key;

        if (columnKey === undefined || columnKey === null || !singleSorter.order) {
            return undefined;
        }

        return { columnKey: String(columnKey), order: singleSorter.order };
    }

    return {
        onTableChange,
    };
}
